import { useEffect } from 'react'
import { useRouter } from 'next/router'

import type { RevealKind } from '@/lib/animation'

import {
    gsap,
    ScrollTrigger,
    registerGsap,
    initialFor,
    settledFor
} from '@/lib/animation'

/** The original's scroll reveals travel 20px; supporting copy travels 10px. */
const DEFAULT_DISTANCE = 20
const DEFAULT_DURATION = 1
const DEFAULT_STAGGER = 0.12
const DEFAULT_PARALLAX = 20

const readKind = (el: HTMLElement): RevealKind => {
    const value = (el.dataset.reveal || 'up').trim()

    return (value === '' ? 'up' : value) as RevealKind
}

const readNumber = (raw: string | undefined, fallback: number): number => {
    if (!raw) return fallback

    const parsed = Number.parseFloat(raw)

    return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Wires every `[data-reveal]` element on the page to a GSAP tween.
 *
 * - `data-reveal`           kind: up | down | left | right | scale | pop | fade | clip
 * - `data-reveal-delay`     seconds before the tween starts
 * - `data-reveal-duration`  seconds, defaults to the original's 1s
 * - `data-reveal-distance`  px offset, defaults to the original's 20px
 * - `data-reveal-rotate`    starting rotation in degrees, for `pop`
 * - `data-reveal-load`      animate on page load instead of on scroll
 * - `data-reveal-group`     on a parent: stagger its `[data-reveal]` children
 * - `data-reveal-stagger`   seconds between staggered children
 * - `data-reveal-start`     ScrollTrigger start, defaults to 'top 85%'
 *
 * `data-parallax="N"` gives an absolutely positioned image wrapper the same
 * treatment the original uses: the frame is grown by 2N px and offset by -N,
 * then scrubbed from +N to -N so it never exposes an edge.
 */
export const useReveals = (): void => {
    const router = useRouter()

    useEffect(() => {
        registerGsap()

        document.documentElement.classList.add('js-anim')

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            gsap.set('[data-reveal]', { clearProps: 'all', opacity: 1 })

            return
        }

        const ctx = gsap.context(() => {
            const groups = gsap.utils.toArray<HTMLElement>('[data-reveal-group]')
            const grouped = new Set<HTMLElement>()

            groups.forEach((group) => {
                const children = gsap.utils
                    .toArray<HTMLElement>('[data-reveal]', group)
                    .filter((child) => child.closest('[data-reveal-group]') === group)

                if (children.length === 0) return

                children.forEach((child) => grouped.add(child))

                const duration = readNumber(group.dataset.revealDuration, DEFAULT_DURATION)
                const delay = readNumber(group.dataset.revealDelay, 0)
                const stagger = readNumber(group.dataset.revealStagger, DEFAULT_STAGGER)

                // Each child keeps its own kind/offset/rotation so a group can
                // mix, e.g. the gallery tiles that pop at different angles.
                children.forEach((child) => {
                    const kind = readKind(child)
                    const distance = readNumber(
                        child.dataset.revealDistance ?? group.dataset.revealDistance,
                        DEFAULT_DISTANCE
                    )
                    const rotate = readNumber(child.dataset.revealRotate, 0)

                    gsap.set(child, initialFor(kind, distance, rotate))
                })

                gsap.to(children, {
                    ...settledFor(readKind(children[0])),
                    duration,
                    delay,
                    stagger,
                    ease: 'springOut',
                    scrollTrigger: {
                        trigger: group,
                        start: group.dataset.revealStart || 'top 85%',
                        once: true
                    }
                })
            })

            const singles = gsap.utils
                .toArray<HTMLElement>('[data-reveal]')
                .filter((el) => !grouped.has(el))

            singles.forEach((el) => {
                const kind = readKind(el)
                const distance = readNumber(el.dataset.revealDistance, DEFAULT_DISTANCE)
                const rotate = readNumber(el.dataset.revealRotate, 0)
                const duration = readNumber(el.dataset.revealDuration, DEFAULT_DURATION)
                const delay = readNumber(el.dataset.revealDelay, 0)
                const onLoad = el.dataset.revealLoad !== undefined

                gsap.set(el, initialFor(kind, distance, rotate))

                gsap.to(el, {
                    ...settledFor(kind),
                    duration,
                    delay,
                    ease: el.dataset.revealEase === 'stiff' ? 'springStiff' : 'springOut',
                    scrollTrigger: onLoad
                        ? undefined
                        : {
                            trigger: el,
                            start: el.dataset.revealStart || 'top 85%',
                            once: true
                        }
                })
            })

            /**
             * Zoomed parallax used by the Nearby bands: the media is scaled up
             * and scrubbed across the overflow that scaling creates. At
             * scale 1.4 the overflow is 40% of the frame, so travelling
             * yPercent +20 to -20 covers it exactly without exposing an edge,
             * which reproduces the original's captured offsets.
             */
            gsap.utils.toArray<HTMLElement>('[data-parallax-zoom]').forEach((el) => {
                const zoom = readNumber(el.dataset.parallaxZoom, 1.4)
                const travel = ((zoom - 1) / 2) * 100

                gsap.set(el, { scale: zoom, transformOrigin: '50% 50%' })

                gsap.fromTo(
                    el,
                    { yPercent: travel },
                    {
                        yPercent: -travel,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: el.closest('[data-parallax-band]') || el.parentElement || el,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                )
            })

            gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((el) => {
                const amount = readNumber(el.dataset.parallax, DEFAULT_PARALLAX)

                gsap.set(el, {
                    top: -amount,
                    height: `calc(100% + ${amount * 2}px)`
                })

                gsap.fromTo(
                    el,
                    { y: amount },
                    {
                        y: -amount,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: el.parentElement || el,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: true
                        }
                    }
                )
            })
        })

        const refresh = (): void => ScrollTrigger.refresh()

        window.addEventListener('load', refresh)

        const fonts = (document as Document & { fonts?: FontFaceSet }).fonts

        if (fonts?.ready) void fonts.ready.then(refresh)

        return () => {
            window.removeEventListener('load', refresh)
            ctx.revert()
        }
    }, [router.asPath])
}
